import { defineStore } from 'pinia'
import Network from '@/Network'
import type { ApprovalFlowDetail } from '@/Network/Types/Responses/ApprovalFlowResponses'
import type {
  ApprovalFlowTemplateDetail,
  GroupedTemplatesResponse
} from '@/Network/Types/Responses/ApprovalFlowTemplateResponses'
import type {
  CreateApprovalFlowRequest,
  UpdateApprovalFlowRequest
} from '@/Network/Types/Requests/ApprovalFlowRequests'
import type { CreateApprovalFlowTemplateRequest } from '@/Network/Types/Requests/ApprovalFlowTemplateRequests'

export const useApprovalFlowStore = defineStore('approvalFlow', {
  state: () => ({
    currentFlow: null as ApprovalFlowDetail | null,
    templates: [] as ApprovalFlowTemplateDetail[],
    groupedTemplates: [] as GroupedTemplatesResponse[],
    isLoading: false,
    isLoadingTemplates: false
  }),

  getters: {
    hasFlow: (state) => state.currentFlow !== null,
    layerCount: (state) => state.currentFlow?.layers.length ?? 0
  },

  actions: {
    async fetchByScheduleId(scheduleId: number) {
      this.isLoading = true
      try {
        this.currentFlow = await Network.approvalFlow.getBySchedule(scheduleId)
      } catch (error) {
        // A action engolia a falha: o único caller (ScheduleApprovalFlowTab) via
        // `currentFlow` nulo e desenhava um fluxo vazio, como se a escala nunca
        // tivesse tido configuração. Agora relança para o caller notificar.
        this.currentFlow = null
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async createFlow(data: CreateApprovalFlowRequest) {
      this.isLoading = true
      try {
        this.currentFlow = await Network.approvalFlow.create(data)
      } finally {
        this.isLoading = false
      }
    },

    async updateFlow(id: number, data: UpdateApprovalFlowRequest) {
      this.isLoading = true
      try {
        this.currentFlow = await Network.approvalFlow.update(id, data)
      } finally {
        this.isLoading = false
      }
    },

    async deleteFlow(id: number) {
      this.isLoading = true
      try {
        await Network.approvalFlow.delete(id)
        this.currentFlow = null
      } finally {
        this.isLoading = false
      }
    },

    async fetchGroupedTemplates(companyId: number) {
      this.isLoadingTemplates = true
      try {
        this.groupedTemplates = await Network.approvalFlow.getTemplatesGroupedByCompany(companyId)
      } catch (error) {
        // Antes: engolia a falha e a aba "Unidades e Escalas" mostrava a lista
        // vazia, como se a empresa não tivesse modelos. Limpa a lista (para não
        // exibir modelos de outra empresa) e relança para o caller notificar.
        this.groupedTemplates = []
        throw error
      } finally {
        this.isLoadingTemplates = false
      }
    },

    async fetchTemplatesBySchedule(scheduleId: number) {
      this.isLoadingTemplates = true
      try {
        this.templates = await Network.approvalFlow.getTemplatesBySchedule(scheduleId)
      } catch (error) {
        // Antes: engolia a falha e a aba "Modelos de Configuração" parecia não
        // ter modelos salvos. Limpa a lista (para não exibir modelos de outra
        // escala) e relança para o caller notificar.
        this.templates = []
        throw error
      } finally {
        this.isLoadingTemplates = false
      }
    },

    async deleteTemplate(id: number, companyId: number) {
      await Network.approvalFlow.deleteTemplate(id, companyId)
      this.templates = this.templates.filter((t) => t.id !== id)
    },

    async createTemplate(
      data: CreateApprovalFlowTemplateRequest
    ): Promise<ApprovalFlowTemplateDetail> {
      this.isLoading = true
      try {
        const created = await Network.approvalFlow.createTemplate(data)
        this.templates.push(created)
        return created
      } finally {
        this.isLoading = false
      }
    },

    async fetchFlowByScheduleIdSilent(scheduleId: number): Promise<ApprovalFlowDetail | null> {
      return await Network.approvalFlow.getBySchedule(scheduleId)
    }
  }
})
